// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import * as yup from 'yup';
import type { StepComponentProps } from '../../types/wizard';

const PROVIDER_OPTIONS = [
  { value: 'Irish Life', text: 'Irish Life' },
  { value: 'Zurich', text: 'Zurich' },
  { value: 'Aviva', text: 'Aviva' },
  { value: 'New Ireland', text: 'New Ireland' },
  { value: 'Standard Life', text: 'Standard Life' },
  { value: 'Royal London', text: 'Royal London' },
  { value: 'Canada Life', text: 'Canada Life' },
  { value: 'Other', text: 'Other' },
];

interface StepProviderDetailsProps extends StepComponentProps {
  policyTypeCategory?: string | null;
}

const providerSchema = yup.object({
  providerContactName: yup.string().trim().required('Name of provider is required'),
  completedByName: yup.string().trim().required('Completed by is required'),
  completedByEmail: yup.string().trim().required('Email is required').email('Please enter a valid email address'),
});

export function StepProviderDetails({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
  policyTypeCategory,
}: StepProviderDetailsProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});

  const isInvestment = policyTypeCategory?.toUpperCase() === 'INVESTMENT';

  const validateField = useCallback((fieldName: string): string | undefined => {
    try {
      providerSchema.validateSyncAt(fieldName, data);
      return undefined;
    } catch (err) {
      if (err instanceof yup.ValidationError) return err.message;
      return undefined;
    }
  }, [data]);

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [validateField]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  const inputClass = (field: string) =>
    `w-full h-[48px] px-md rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-black font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`;

  const selectClass = (field: string) =>
    `w-full h-[48px] px-md pr-xxl rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`;

  const chevronIcon = (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  if (isInvestment) {
    return (
      <div className="flex flex-col gap-lg">
        <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
          Provider details
        </div>

        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Completed by (name) <span className="text-[#E7000B]">*</span>
            </div>
            <input
              type="text"
              placeholder="Completed by (name)"
              autoComplete="name"
              value={(data.completedByName as string) || ''}
              onChange={(e) => {
                onFieldChange('completedByName', e.target.value);
                onClearFieldError('completedByName');
              }}
              onBlur={() => handleBlur('completedByName')}
              className={inputClass('completedByName')}
            />
            {getError('completedByName') && (
              <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('completedByName')}</div>
            )}
          </div>

          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Organisation name <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <select
                value={(data.providerContactName as string) || ''}
                onChange={(e) => {
                  onFieldChange('providerContactName', e.target.value);
                  onClearFieldError('providerContactName');
                }}
                onBlur={() => handleBlur('providerContactName')}
                className={selectClass('providerContactName')}
              >
                <option value="" disabled className="text-[#B6B6B6]">Choose a provider</option>
                {PROVIDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              {chevronIcon}
            </div>
            {getError('providerContactName') && (
              <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('providerContactName')}</div>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              Email contact <span className="text-[#E7000B]">*</span>
            </div>
            <input
              type="email"
              placeholder="Email contact"
              autoComplete="email"
              value={(data.completedByEmail as string) || ''}
              onChange={(e) => {
                onFieldChange('completedByEmail', e.target.value);
                onClearFieldError('completedByEmail');
              }}
              onBlur={() => handleBlur('completedByEmail')}
              className={inputClass('completedByEmail')}
            />
            {getError('completedByEmail') && (
              <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('completedByEmail')}</div>
            )}
          </div>

          <div className="flex-1" />
        </div>
      </div>
    );
  }

  // Pension flow (original layout)
  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Provider details
      </div>

      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Name of provider <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            placeholder="Name of provider"
            value={(data.providerContactName as string) || ''}
            onChange={(e) => {
              onFieldChange('providerContactName', e.target.value);
              onClearFieldError('providerContactName');
            }}
            onBlur={() => handleBlur('providerContactName')}
            className={inputClass('providerContactName')}
          />
          {getError('providerContactName') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('providerContactName')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Completed by (name) <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="text"
            placeholder="Completed by (name)"
            autoComplete="name"
            value={(data.completedByName as string) || ''}
            onChange={(e) => {
              onFieldChange('completedByName', e.target.value);
              onClearFieldError('completedByName');
            }}
            onBlur={() => handleBlur('completedByName')}
            className={inputClass('completedByName')}
          />
          {getError('completedByName') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('completedByName')}</div>
          )}
        </div>
      </div>

      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Email contact <span className="text-[#E7000B]">*</span>
          </div>
          <input
            type="email"
            placeholder="Email contact"
            autoComplete="email"
            value={(data.completedByEmail as string) || ''}
            onChange={(e) => {
              onFieldChange('completedByEmail', e.target.value);
              onClearFieldError('completedByEmail');
            }}
            onBlur={() => handleBlur('completedByEmail')}
            className={inputClass('completedByEmail')}
          />
          {getError('completedByEmail') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('completedByEmail')}</div>
          )}
        </div>

        <div className="flex-1" />
      </div>
    </div>
  );
}
